import { useEffect, useState } from 'react';

import { Card, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';

export function SettingsPage() {
  const { me, can, refresh } = useAuth();
  const settings = useAsync(() => api.alerts.settings(), []);

  const [undercut, setUndercut] = useState(8);
  const [mispricing, setMispricing] = useState(10);
  const [discordUrl, setDiscordUrl] = useState('');
  const [offset, setOffset] = useState(0);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings.data) {
      setUndercut(settings.data.settings.undercutThresholdPct);
      setMispricing(settings.data.settings.mispricingThresholdPct);
    }
  }, [settings.data]);

  useEffect(() => {
    if (me) {
      setDiscordUrl(me.subscriber.discordWebhookUrl ?? '');
      setOffset(me.subscriber.timezoneOffset);
    }
  }, [me]);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await api.alerts.updateSettings({
        undercutThresholdPct: undercut,
        mispricingThresholdPct: mispricing,
        discordWebhookUrl: discordUrl.trim() === '' ? null : discordUrl.trim(),
        channels: discordUrl.trim() && can('alerts.discord') ? ['in_app', 'discord'] : ['in_app'],
      });
      await api.auth.updateProfile({ timezoneOffset: offset });
      await refresh();
      settings.reload();
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  if (settings.loading) return <Spinner />;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Settings</h1>
          <p>Thresholds, delivery channels and how times are displayed.</p>
        </div>
      </header>

      <Card title="Alert thresholds">
        <div className="stack" style={{ maxWidth: 460 }}>
          <div className="field">
            <label htmlFor="undercut">Undercut alert threshold ({undercut}%)</label>
            <input
              id="undercut"
              type="range"
              min={1}
              max={50}
              value={undercut}
              onChange={(e) => setUndercut(Number(e.target.value))}
            />
            <div className="small muted">
              Alert when the market median sits this far below your price. Lower means more alerts;
              below about 5% you will mostly hear about ordinary price noise.
            </div>
          </div>

          <div className="field">
            <label htmlFor="mispricing">Mispricing threshold ({mispricing}%)</label>
            <input
              id="mispricing"
              type="range"
              min={1}
              max={50}
              value={mispricing}
              onChange={(e) => setMispricing(Number(e.target.value))}
            />
            <div className="small muted">
              How far from the benchmark an item must sit before it is called mispriced rather than
              competitive.
            </div>
          </div>
        </div>
      </Card>

      <Card title="Discord delivery">
        <div className="stack" style={{ maxWidth: 560 }}>
          {!can('alerts.discord') && (
            <Notice>
              Discord delivery is included with Pro. You can save a webhook now and it will start
              being used as soon as you upgrade.
            </Notice>
          )}
          <div className="field">
            <label htmlFor="discord">Discord webhook URL</label>
            <input
              id="discord"
              value={discordUrl}
              placeholder="https://discord.com/api/webhooks/..."
              onChange={(e) => setDiscordUrl(e.target.value)}
            />
            <div className="small muted">
              Create it in your own server under Channel Settings, Integrations, Webhooks. A failed
              post is retried on the next run and never blocks the in-app alert.
            </div>
          </div>
        </div>
      </Card>

      <Card title="Display">
        <div className="stack" style={{ maxWidth: 320 }}>
          <div className="field">
            <label htmlFor="offset">Hour offset from UTC ({offset >= 0 ? '+' : ''}{offset})</label>
            <input
              id="offset"
              type="number"
              min={-12}
              max={14}
              value={offset}
              onChange={(e) => setOffset(Number(e.target.value))}
            />
            <div className="small muted">
              Shifts the demand heatmap so peak windows read in the time you think in.
            </div>
          </div>
        </div>
      </Card>

      <div className="row" style={{ marginTop: 16 }}>
        <button className="primary" onClick={save} disabled={busy}>
          {busy ? 'Saving...' : 'Save settings'}
        </button>
        {saved && <span className="delta-up small">Saved</span>}
      </div>
    </>
  );
}
